import { useState, useMemo } from "react";
import { ChevronDown, ChevronUp, User, Clock, MessageCircle, Filter } from "lucide-react";

/**
 * NovaVotesHistory — desplegable con quién votó, cuándo, sus 6 scores y comentario.
 * Props:
 *   votes: [{user_name, user_id, scores:{key:val}, comment, updated_at}]
 *   criteria: [{key,label,weight}]
 */
export default function NovaVotesHistory({ votes = [], criteria = [] }) {
  const [open, setOpen] = useState(false);
  const [voterFilter, setVoterFilter] = useState("");

  const uniqueVoters = useMemo(() => {
    const set = new Map();
    votes.forEach((v) => {
      const name = v.user_name || v.user_id || "—";
      set.set(name, name);
    });
    return Array.from(set.keys()).sort();
  }, [votes]);

  const filtered = useMemo(() => (
    voterFilter ? votes.filter((v) => (v.user_name || v.user_id || "—") === voterFilter) : votes
  ), [votes, voterFilter]);

  if (!votes.length) return null;

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 overflow-hidden" data-testid="votes-history">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        data-testid="votes-history-toggle"
      >
        <span className="flex items-center gap-1.5">
          <User className="w-3.5 h-3.5"/> Historial de votos ({votes.length})
          {voterFilter && <span className="ml-1 text-[10px] text-emerald-600">· filtrado: {voterFilter}</span>}
        </span>
        {open ? <ChevronUp className="w-3.5 h-3.5"/> : <ChevronDown className="w-3.5 h-3.5"/>}
      </button>
      {open && (
        <div className="border-t border-slate-200 dark:border-slate-800">
          {uniqueVoters.length > 1 && (
            <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-800 bg-slate-100/60 dark:bg-slate-900/40 flex items-center gap-2">
              <Filter className="w-3.5 h-3.5 text-muted-foreground"/>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Filtrar por votante:</span>
              <select
                value={voterFilter}
                onChange={(e) => setVoterFilter(e.target.value)}
                className="text-xs px-2 py-0.5 rounded border border-border bg-background flex-1 max-w-[220px]"
                data-testid="votes-history-voter-filter"
              >
                <option value="">Todos ({votes.length})</option>
                {uniqueVoters.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              {voterFilter && (
                <button onClick={() => setVoterFilter("")} className="text-[10px] text-emerald-600 hover:underline" data-testid="votes-history-clear-filter">
                  limpiar
                </button>
              )}
            </div>
          )}
          <div className="divide-y divide-slate-200 dark:divide-slate-800">
          {filtered.map((v, i) => (
            <div key={v.id || i} className="px-3 py-2.5 space-y-1.5" data-testid={`votes-history-row-${i}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs font-semibold text-slate-900 dark:text-slate-100 truncate">
                  {v.user_name || v.user_id || "—"}
                </div>
                <div className="text-[10px] text-muted-foreground flex items-center gap-1 flex-shrink-0">
                  <Clock className="w-3 h-3"/>
                  {(v.updated_at || v.created_at || "").slice(0, 16).replace("T", " ")}
                </div>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-1">
                {criteria.map((c) => {
                  const val = Number(v.scores?.[c.key] ?? 0);
                  return (
                    <div key={c.key} className="text-center" title={c.label}>
                      <div className="text-[9px] text-muted-foreground uppercase tracking-wider truncate">{c.label.slice(0, 8)}</div>
                      <div className="text-xs font-bold text-emerald-700 dark:text-emerald-300">{val}</div>
                      <div className="h-1 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                        <div className="h-full bg-emerald-500" style={{ width: `${(val / 5) * 100}%` }}/>
                      </div>
                    </div>
                  );
                })}
              </div>
              {v.comment && (
                <div className="text-[11px] text-slate-600 dark:text-slate-300 flex gap-1.5 pt-1 border-t border-slate-200 dark:border-slate-800">
                  <MessageCircle className="w-3 h-3 flex-shrink-0 mt-0.5 text-slate-400"/>
                  <span className="italic">&ldquo;{v.comment}&rdquo;</span>
                </div>
              )}
            </div>
          ))}
          </div>
        </div>
      )}
    </div>
  );
}
